import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { CartLine, MenuItem } from '../types';

interface CartContextValue {
  lines: CartLine[];
  count: number;
  subtotal: number;
  add(item: MenuItem, qty?: number, notes?: string): void;
  remove(menuItemId: string): void;
  setQuantity(menuItemId: string, qty: number): void;
  clear(): void;
}

const CartContext = createContext<CartContextValue | null>(null);
const STORAGE_KEY = 'saheeh_customer_cart_v1';

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartLine[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(lines)); } catch { /* ignore */ }
  }, [lines]);

  const add = useCallback((item: MenuItem, qty = 1, notes?: string) => {
    setLines((prev) => {
      // If notes differ, treat as a new line (so two pizzas with different
      // notes stay separate). With no notes, merge into the existing line.
      const existing = prev.find(
        (l) => l.menu_item_id === item.id && (l.notes || '') === (notes || ''),
      );
      if (existing) {
        return prev.map((l) =>
          l === existing ? { ...l, quantity: l.quantity + qty } : l,
        );
      }
      return [
        ...prev,
        {
          menu_item_id: item.id,
          name: item.name,
          unit_price: Number(item.base_price) || 0,
          quantity: qty,
          notes: notes || undefined,
          image_url: item.image_url,
        },
      ];
    });
  }, []);

  const remove = useCallback((menuItemId: string) => {
    setLines((prev) => prev.filter((l) => l.menu_item_id !== menuItemId));
  }, []);

  const setQuantity = useCallback((menuItemId: string, qty: number) => {
    setLines((prev) =>
      qty <= 0
        ? prev.filter((l) => l.menu_item_id !== menuItemId)
        : prev.map((l) => (l.menu_item_id === menuItemId ? { ...l, quantity: qty } : l)),
    );
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      count: lines.reduce((s, l) => s + l.quantity, 0),
      subtotal: lines.reduce((s, l) => s + l.unit_price * l.quantity, 0),
      add, remove, setQuantity, clear,
    }),
    [lines, add, remove, setQuantity, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
